import axios from 'axios';
import { getApiUrl } from './apiBase';

const API_URL = getApiUrl();

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: 'buyer' | 'seller';
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    profilePicture?: string;
    wechatId?: string;
    phoneNumber?: string;
    eduVerified?: boolean;
    school?: string;
    schoolDomain?: string;
  };
}

class AuthService {
  private useMockData = false; // Real backend enabled (LAUNCH.md Phase 1)

  async login(data: LoginData): Promise<AuthResponse> {
    if (this.useMockData) {
      // Mock authentication for development
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
      
      let mockUser;
      if (data.email === 'buyer@demo.com' && data.password === 'password') {
        mockUser = {
          id: '1',
          email: 'buyer@demo.com',
          firstName: 'John',
          lastName: 'Buyer',
          role: 'buyer'
        };
      } else if (data.email === 'seller@demo.com' && data.password === 'password') {
        mockUser = {
          id: '2',
          email: 'seller@demo.com',
          firstName: 'Jane',
          lastName: 'Seller',
          role: 'seller'
        };
      } else {
        throw new Error('Invalid credentials');
      }
      
      const response = {
        token: 'mock_jwt_token_' + mockUser.role,
        user: mockUser
      };
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      return response;
    }
    
    const response = await axios.post(`${API_URL}/auth/login`, data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    if (this.useMockData) {
      // Mock registration for development
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
      
      const mockUser = {
        id: Date.now().toString(),
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role || 'buyer'
      };
      
      const response = {
        token: 'mock_jwt_token_' + mockUser.role,
        user: mockUser
      };
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      return response;
    }
    
    const response = await axios.post(`${API_URL}/auth/register`, data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  async getCurrentUserFromApi() {
    const token = this.getToken();
    
    if (this.useMockData) {
      // In mock mode, always trust localStorage if token and user exist
      const user = localStorage.getItem('user');
      if (token && user) {
        try {
          const parsedUser = JSON.parse(user);
          console.log('Mock auth: Using stored user:', parsedUser);
          return parsedUser;
        } catch (error) {
          console.error('Mock auth: Error parsing stored user:', error);
          return null;
        }
      }
      console.log('Mock auth: No token or user found');
      return null;
    }

    if (!token) {
      console.log('No auth token found');
      return null;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && [401, 403].includes(error.response?.status || 0)) {
        this.logout();
        return null;
      }
      // Don't logout automatically - this prevents users from being kicked out 
      // when the backend is unavailable. In production, you may want to check
      // for specific error codes (401, 403) before logging out.
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    }
  }

  async updateProfile(data: Partial<Pick<AuthResponse['user'], 'firstName' | 'lastName' | 'phoneNumber' | 'wechatId'>>) {
    const token = this.getToken();
    const response = await axios.put(`${API_URL}/auth/me`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data as AuthResponse['user'];
  }

  async requestPasswordReset(email: string): Promise<void> {
    await axios.post(`${API_URL}/auth/forgot-password`, { email });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await axios.post(`${API_URL}/auth/reset-password`, { token, password });
  }

  async sendEduCode() {
    const token = this.getToken();
    const response = await axios.post(
      `${API_URL}/auth/edu/send-code`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }

  async verifyEduCode(code: string) {
    const token = this.getToken();
    const response = await axios.post(
      `${API_URL}/auth/edu/verify`,
      { code },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }
}

export const authService = new AuthService();
